import { useState, useRef } from 'react';
import './versionlog.css';
import { api } from '../../api/client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNotify } from '../../components/notifications';
import { useSaveFlow, useDeleteFlow } from '../../components/confirm';
import { FormTitle } from '../../components/FormTitle';
import { AppToolbar } from '../../components/AppToolbar';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../components/AppDataGrid';
import { GridSearch } from '../../components/GridSearch';
import { useFetchMs } from '../../hooks/useFetchMs';
import * as pdfjsLib from 'pdfjs-dist';
// @ts-ignore — Vite ?url suffix gives us a local asset URL for the worker
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

// ── Types ────────────────────────────────────────────────────────────────────

interface VersionLogListItem {
  oid: string;
  versionNumber: string | null;
  xafVersionNumber: string | null;
  dateReleased: string | null;
  releasedBy: string | null;
  dateTested: string | null;
  testedBy: string | null;
  ticketId: string | null;
  attachment: string | null;
}

interface VersionPostItem {
  oid: string;
  ticketId: string | null;
  category: number;
  categoryLabel: string | null;
  comment: string | null;
  subject: string | null;
  author: string | null;
  postedDateTime: string | null;
}

interface VersionLogDetail extends VersionLogListItem {
  description: string | null;
  posts: VersionPostItem[];
}

interface VersionLogInput {
  versionNumber: string;
  xafVersionNumber: string;
  description: string;
  dateReleased: string;
  releasedBy: string;
  dateTested: string;
  testedBy: string;
  ticketId: string;
}

interface VersionPostInput {
  ticketId: string;
  category: number;
  comment: string;
  subject: string;
}

// ── API ──────────────────────────────────────────────────────────────────────

const vlApi = {
  list: () => api.get<{ success: boolean; data: VersionLogListItem[] }>('/api/v1/version-log'),
  getById: (id: string) =>
    api.get<{ success: boolean; data: VersionLogDetail }>(`/api/v1/version-log/${id}`),
  create: (input: VersionLogInput) =>
    api.post<{ success: boolean; data: { id: string } }>('/api/v1/version-log', input),
  update: (id: string, input: VersionLogInput) =>
    api.put<{ success: boolean }>(`/api/v1/version-log/${id}`, input),
  delete: (id: string) =>
    api.delete<{ success: boolean }>(`/api/v1/version-log/${id}`),
  addPost: (id: string, input: VersionPostInput) =>
    api.post<{ success: boolean; data: { id: string } }>(`/api/v1/version-log/${id}/posts`, input),
  deletePost: (id: string, postId: string) =>
    api.delete<{ success: boolean }>(`/api/v1/version-log/${id}/posts/${postId}`),
  uploadAttachment: (id: string, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post<{ success: boolean; data: { path: string; fileName: string } }>(
      `/api/v1/version-log/${id}/attachment`, fd,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
  },
  downloadAttachment: (id: string) =>
    api.get(`/api/v1/version-log/${id}/attachment`, { responseType: 'blob' }),
};

const CATEGORY_OPTIONS = [
  { value: 0, label: 'New Feature' },
  { value: 1, label: 'Enhancement' },
  { value: 2, label: 'Bug Fixing' },
];

const EMPTY_INPUT: VersionLogInput = {
  versionNumber: '',
  xafVersionNumber: '',
  description: '',
  dateReleased: '',
  releasedBy: '',
  dateTested: '',
  testedBy: '',
  ticketId: '',
};

const EMPTY_POST: VersionPostInput = { ticketId: '', category: 0, comment: '', subject: '' };

// ── Page ─────────────────────────────────────────────────────────────────────

/** Maintenance → Security → Version Log */
export default function VersionLogPage() {
  const qc = useQueryClient();
  const notify = useNotify();
  const runSave = useSaveFlow();
  const runDelete = useDeleteFlow();

  // list data
  const { data: listData, isFetching } = useQuery({
    queryKey: ['version-log'],
    queryFn: () => vlApi.list().then(r => r.data.data),
  });
  const loadMs = useFetchMs(isFetching);
  const rows = listData ?? [];

  // search
  const [search, setSearch] = useState('');
  const term = search.toLowerCase().trim();
  const filteredRows = term
    ? rows.filter(r =>
        (r.versionNumber ?? '').toLowerCase().includes(term) ||
        (r.xafVersionNumber ?? '').toLowerCase().includes(term) ||
        (r.releasedBy ?? '').toLowerCase().includes(term) ||
        (r.testedBy ?? '').toLowerCase().includes(term) ||
        (r.ticketId ?? '').toLowerCase().includes(term))
    : rows;

  // reset selection when search changes so a filtered-out row can't stay "selected"
  const handleSearch = (v: string) => setSearch(v);

  // shared-grid selection (checkbox column + row click) — ONE Set drives both
  // Edit (exactly one selected) and Delete (one or more selected).
  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());
  const effectiveSelectedId = selectedIds.size === 1 ? [...selectedIds][0] as string : null;
  const canEdit = selectedIds.size === 1;
  const canDelete = selectedIds.size > 0;

  // The grid's OWN column-filtered + sorted set (pre-pagination) — kept in sync
  // via onVisibleRowsChange so "Export to" exports exactly what's on screen,
  // including any active column filter (not just the search-filtered rows).
  const [visibleRows, setVisibleRows] = useState<VersionLogListItem[]>(filteredRows);

  // detail dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null); // null = New
  const [form, setForm] = useState<VersionLogInput>(EMPTY_INPUT);
  const [detail, setDetail] = useState<VersionLogDetail | null>(null);
  const [errors, setErrors] = useState<Partial<VersionLogInput>>({});

  // add-post dialog
  const [postDlgOpen, setPostDlgOpen] = useState(false);
  const [postForm, setPostForm] = useState<VersionPostInput>(EMPTY_POST);

  // attachment / PDF state
  const [attachFileName, setAttachFileName] = useState('');
  const [pdfParsing, setPdfParsing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Parse PDF and extract posts — one post per table row (Tracker ID + Description)
  const handlePdfFile = async (file: File) => {
    setAttachFileName(file.name);
    setPdfParsing(true);
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

      // Extract text as visual lines by grouping items with the same y-coordinate per page
      const lines: string[] = [];
      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        const page = await pdf.getPage(pageNum);
        const content = await page.getTextContent();

        const lineMap = new Map<number, { x: number; str: string }[]>();
        for (const item of content.items as any[]) {
          const str = (item.str ?? '').trim();
          if (!str) continue;
          const y = Math.round(item.transform[5]);
          if (!lineMap.has(y)) lineMap.set(y, []);
          lineMap.get(y)!.push({ x: item.transform[4], str });
        }

        // Sort y descending (PDF y-axis is bottom-up), x ascending within each line
        const sortedYs = [...lineMap.keys()].sort((a, b) => b - a);
        for (const y of sortedYs) {
          const joined = lineMap.get(y)!
            .sort((a, b) => a.x - b.x)
            .map(i => i.str)
            .join(' ')
            .trim();
          if (joined) lines.push(joined);
        }
      }

      // Parse entries from lines
      const parsedPosts: VersionPostInput[] = [];
      const seen = new Set<string>();

      // Track current section category from headings
      let currentCategory = 0;

      // Accumulate multi-line descriptions
      let pendingTicket = '';
      let pendingCategory = 0;
      let pendingComment = '';

      const flushPending = () => {
        if (pendingTicket && !seen.has(pendingTicket)) {
          seen.add(pendingTicket);
          parsedPosts.push({
            ticketId: pendingTicket,
            category: pendingCategory,
            comment: pendingComment.replace(/\s+/g, ' ').trim().slice(0, 500),
            subject: '',
          });
        }
        pendingTicket = '';
        pendingComment = '';
      };

      for (const line of lines) {
        // Detect section heading → update current category
        if (/bug\s*fix/i.test(line) && !/no\.\s*tracker/i.test(line)) {
          flushPending(); currentCategory = 2; continue;
        }
        if (/enhancement/i.test(line) && !/no\.\s*tracker/i.test(line)) {
          flushPending(); currentCategory = 1; continue;
        }
        if (/new\s*feature/i.test(line) && !/no\.\s*tracker/i.test(line)) {
          flushPending(); currentCategory = 0; continue;
        }

        // Skip table header row and version summary lines
        if (/no\.\s*tracker\s*id/i.test(line)) continue;
        if (/version\s*tracker\s*id\s*:/i.test(line)) continue;
        if (/approved\s*by\s*:/i.test(line)) continue;
        if (/release\s*date\s*:/i.test(line)) continue;
        if (/released\s*by\s*:/i.test(line)) continue;
        if (/document\s*no\s*:/i.test(line)) continue;
        if (/^version\s*:/i.test(line)) continue;

        // Table data row: "<row_no>  <tracker_id>  <description>"
        // row_no = 1-2 digits, tracker_id = 3-6 digits
        const rowMatch = line.match(/^\s*(\d{1,2})\s+(\d{3,6})\s+(.+)$/);
        if (rowMatch) {
          flushPending();
          pendingTicket   = rowMatch[2] ?? '';
          pendingCategory = currentCategory;
          pendingComment  = rowMatch[3] ?? '';
          continue;
        }

        // Continuation line (wrapped description from previous row)
        if (pendingTicket && line.trim() && !/^\s*\d{1,2}\s*$/.test(line)) {
          pendingComment += ' ' + line.trim();
        }
      }
      flushPending();

      if (parsedPosts.length === 0) {
        notify.warning('No ticket entries found in the PDF. Check the PDF format.');
        return;
      }

      // Upload the PDF file to the server (attachment path stored in DB)
      if (editId) {
        try {
          await vlApi.uploadAttachment(editId, file);
          // Refresh detail so the attachment field reflects the new path
          const refreshed = await vlApi.getById(editId);
          setDetail(prev => prev
            ? { ...refreshed.data.data, posts: prev.posts }
            : refreshed.data.data
          );
        } catch {
          notify.warning('PDF uploaded locally but could not save to server. Check API.');
        }
      }

      // Add parsed posts to local state only — they will be saved when Save is clicked
      const newPostItems: VersionPostItem[] = parsedPosts.map((p, i) => ({
        oid: `__pending__${Date.now()}_${i}`,
        ticketId: p.ticketId,
        category: p.category,
        categoryLabel: CATEGORY_OPTIONS.find(o => o.value === p.category)?.label ?? '',
        comment: p.comment,
        subject: p.subject,
        author: 'Service',
        postedDateTime: null,
      }));

      setDetail(prev => prev
        ? { ...prev, posts: [...(prev.posts ?? []), ...newPostItems] }
        : prev
      );
      notify.success(`${parsedPosts.length} post(s) loaded from PDF. Click Save to persist.`);
    } catch (err) {
      console.error('PDF parse error:', err);
      notify.error('Failed to parse PDF. Please check the file and try again.');
    } finally {
      setPdfParsing(false);
    }
  };

  // mutations
  const createMut = useMutation({ mutationFn: (inp: VersionLogInput) => vlApi.create(inp) });
  const updateMut = useMutation({ mutationFn: ({ id, inp }: { id: string; inp: VersionLogInput }) => vlApi.update(id, inp) });
  const deleteMut = useMutation({ mutationFn: (id: string) => vlApi.delete(id) });
  const addPostMut = useMutation({
    mutationFn: ({ id, inp }: { id: string; inp: VersionPostInput }) => vlApi.addPost(id, inp),
  });
  const delPostMut = useMutation({
    mutationFn: ({ id, postId }: { id: string; postId: string }) => vlApi.deletePost(id, postId),
  });

  // ── helpers ────────────────────────────────────────────────────────────────

  const openNew = () => {
    setEditId(null);
    setDetail(null);
    setForm(EMPTY_INPUT);
    setErrors({});
    setDialogOpen(true);
  };

  const openEdit = async (id: string | null) => {
    if (!id) return;
    try {
      const res = await vlApi.getById(id);
      const d = res.data.data;
      setDetail(d);
      setEditId(id);
      setForm({
        versionNumber:    d.versionNumber ?? '',
        xafVersionNumber: d.xafVersionNumber ?? '',
        description:      d.description ?? '',
        dateReleased:     d.dateReleased ? d.dateReleased.substring(0, 10) : '',
        releasedBy:       d.releasedBy ?? '',
        dateTested:       d.dateTested ? d.dateTested.substring(0, 10) : '',
        testedBy:         d.testedBy ?? '',
        ticketId:         d.ticketId ?? '',
      });
      setErrors({});
      // Show existing attachment filename if present
      if (d.attachment) {
        const parts = d.attachment.split('/');
        setAttachFileName(parts[parts.length - 1] ?? d.attachment);
      } else {
        setAttachFileName('');
      }
      setDialogOpen(true);
    } catch {
      notify.error('Failed to load version log detail.');
    }
  };

  const closeDialog = () => { setDialogOpen(false); setDetail(null); setAttachFileName(''); };

  const validate = (): boolean => {
    const e: Partial<VersionLogInput> = {};
    if (!form.versionNumber.trim()) e.versionNumber = 'Required';
    if (!form.ticketId.trim())      e.ticketId      = 'Required';
    if (!form.description.trim())   e.description   = 'Required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = () => {
    if (!validate()) return;
    runSave({
      confirmTitle: editId ? 'Update' : 'Save',
      confirmMessage: editId ? 'Do you want to Update?' : 'Do you want to Save?',
      save: async () => {
        let savedId = editId;
        if (editId) {
          await updateMut.mutateAsync({ id: editId, inp: form });
        } else {
          const res = await createMut.mutateAsync(form);
          savedId = res.data.data.id;
        }
        // Save any pending posts that came from PDF (oid starts with __pending__)
        if (savedId) {
          const pending = (detail?.posts ?? []).filter(p => p.oid.startsWith('__pending__'));
          for (const p of pending) {
            try {
              await vlApi.addPost(savedId, {
                ticketId: p.ticketId ?? '',
                category: p.category,
                comment: p.comment ?? '',
                subject: p.subject ?? '',
              });
            } catch { /* skip individual failures */ }
          }
        }
        qc.invalidateQueries({ queryKey: ['version-log'] });
      },
      successMessage: editId
        ? `Version ${form.versionNumber} updated successfully.`
        : `Version ${form.versionNumber} saved successfully.`,
      onSaved: () => { closeDialog(); setSelectedIds(new Set()); },
      onError: (e: unknown) =>
        notify.error((e as { message?: string })?.message ?? 'Save failed.'),
      // Reason-for-change gate (standing rule 11) — Create stays exempt.
      formName: 'Version Log',
      auditAction: editId ? 'edit' : 'create',
    });
  };

  const handleDelete = async () => {
    if (!canDelete) return;
    const idsToDelete = [...selectedIds] as string[];
    if (idsToDelete.length === 0) return;

    const label = idsToDelete.length === 1
      ? `version "${rows.find(r => r.oid === idsToDelete[0])?.versionNumber ?? idsToDelete[0]}"`
      : `${idsToDelete.length} selected version(s)`;

    await runDelete({
      formName: 'Version Log',
      message: `Delete ${label}?`,
      delete: async () => {
        let failed = 0;
        for (const id of idsToDelete) {
          try { await deleteMut.mutateAsync(id); } catch { failed++; }
        }
        if (failed > 0) throw new Error(`${failed} of ${idsToDelete.length} failed to delete.`);
      },
      successMessage: idsToDelete.length === 1 ? 'Deleted.' : `${idsToDelete.length} versions deleted.`,
      onDeleted: () => {
        qc.invalidateQueries({ queryKey: ['version-log'] });
        setSelectedIds(new Set());
      },
    });
  };

  const handleAddPost = () => {
    if (!editId) return;
    addPostMut.mutate({ id: editId, inp: postForm }, {
      onSuccess: async () => {
        setPostDlgOpen(false);
        setPostForm(EMPTY_POST);
        // Refresh detail posts
        const res = await vlApi.getById(editId);
        setDetail(res.data.data);
      },
      onError: () => notify.error('Failed to add post.'),
    });
  };

  const handleDeletePost = async (postId: string) => {
    if (!editId) return;
    await runDelete({
      message: 'Remove this post?',
      delete: async () => { await delPostMut.mutateAsync({ id: editId, postId }); },
      successMessage: null, // sub-record removal — keep it quiet, no modal ack
      onDeleted: async () => {
        const res = await vlApi.getById(editId);
        setDetail(res.data.data);
      },
      onError: () => notify.error('Failed to remove post.'),
    });
  };

  const field = (key: keyof VersionLogInput) => ({
    value: form[key] as string,
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm(f => ({ ...f, [key]: e.target.value })),
    className: errors[key] ? 'err' : undefined,
  });

  // ── Attachment download ────────────────────────────────────────────────────
  const handleDownload = async (e: React.MouseEvent, row: VersionLogListItem) => {
    e.stopPropagation();
    if (!row.attachment) return;
    try {
      const res = await vlApi.downloadAttachment(row.oid);
      const fileName = row.attachment.split('/').pop() ?? `${row.versionNumber ?? row.oid}.pdf`;
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement('a');
      a.href = url; a.download = fileName; a.click();
      URL.revokeObjectURL(url);
    } catch {
      notify.error('Failed to download attachment.');
    }
  };

  // ── Shared grid columns (also drive the shared "Export to") ────────────────
  const columns: AppColumn<VersionLogListItem>[] = [
    { field: 'versionNumber', header: 'Version Number' },
    {
      field: 'attachment', header: 'Version File', width: 100, align: 'center',
      sortable: false, filterable: false, noExport: true,
      render: (r) => r.attachment ? (
        <button className="vl-ver-link" title="Download PDF" onClick={(e) => handleDownload(e, r)}>
          <i className="ti ti-file-type-pdf" style={{ fontSize: 18, color: '#c0392b' }} />
        </button>
      ) : null,
    },
    { field: 'dateReleased', header: 'Date Released', type: 'date' },
    { field: 'releasedBy', header: 'Released By' },
    { field: 'dateTested', header: 'Date Tested', type: 'date' },
    { field: 'testedBy', header: 'Tested By' },
    { field: 'ticketId', header: 'Version Ticket ID' },
  ];

  // ── render ─────────────────────────────────────────────────────────────────

  return (
    <div className="vl-page">
      {/* Breadcrumb + the ONE shared FormTitle (icon/colour from navData, top
          load-time chip, and the quiet audit-trail chip) — CLAUDE.md §18.6. */}
      <div className="vl-bc">
        <span>Home</span>
        <i className="ti ti-chevron-right" />
        <span>Maintenance</span>
        <i className="ti ti-chevron-right" />
        <span>Security</span>
        <i className="ti ti-chevron-right" />
        <span className="active">Version Log</span>
      </div>
      <FormTitle pageId="maint-versionlog" auditFormName="Version Log" loadMs={loadMs} loading={isFetching} />
      <div className="vl-sub">{filteredRows.length} item{filteredRows.length === 1 ? '' : 's'}</div>

      {/* Toolbar on its OWN full-width row (BO UI Standard §6) — same 8px
          bottom margin every other list page uses (e.g. Custom Reporting). */}
      <div className="vl-body">
        {/* Shared action bar — canonical New · Edit · Delete · Refresh · Export to
            order, the SAME .ca-btn style + icons as every other form. */}
        <div style={{ margin: '0 0 8px' }}>
          <AppToolbar
            onNew={openNew}
            onEdit={() => openEdit(effectiveSelectedId)}
            editDisabled={!canEdit}
            onDelete={handleDelete}
            deleteDisabled={!canDelete}
            onRefresh={() => qc.invalidateQueries({ queryKey: ['version-log'] })}
            refreshing={isFetching}
            exportData={{ filename: 'Version Log', ...gridExportMatrix(visibleRows, columns) }}
          />
        </div>

        {/* Search — the shared <GridSearch> component (components/GridSearch.tsx). */}
        <GridSearch value={search} onChange={handleSearch} />

        {/* Shared grid — sort, per-column filters, resizable columns, one footer. */}
        <AppDataGrid<VersionLogListItem>
          rows={filteredRows}
          columns={columns}
          getRowId={(r) => r.oid}
          loading={isFetching}
          emptyText={search ? 'No results match your search.' : 'No version log entries found.'}
          defaultPageSize={100}
          onVisibleRowsChange={setVisibleRows}
          selectable
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
          onRowClick={(r) => setSelectedIds(prev => (prev.size === 1 && prev.has(r.oid)) ? new Set() : new Set([r.oid]))}
          onRowDoubleClick={(r) => openEdit(r.oid)}
        />
      </div>

      {/* ── Detail dialog ── */}
      {dialogOpen && (
        <div className="vl-overlay" onMouseDown={closeDialog}>
          <div className="vl-dialog" onMouseDown={e => e.stopPropagation()}>
            <div className="vl-dialog-hdr">
              <i className="ti ti-versions" />
              <span>{editId ? 'Edit Version Log' : 'New Version Log'}</span>
              <i className="ti ti-x vl-dialog-x" onClick={closeDialog} />
            </div>

            <div className="vl-dialog-body">
              {/* Header fields */}
              <div className="vl-form-grid">
                <div className="vl-field">
                  <label>Version Number<span className="req">*</span></label>
                  <input {...field('versionNumber')} placeholder="e.g. 26.6.3" />
                  {errors.versionNumber && <span className="vl-field-err">{errors.versionNumber}</span>}
                </div>
                <div className="vl-field">
                  <label>Version Ticket ID<span className="req">*</span></label>
                  <input {...field('ticketId')} placeholder="Ticket number" />
                  {errors.ticketId && <span className="vl-field-err">{errors.ticketId}</span>}
                </div>
                <div className="vl-field">
                  <label>Released By</label>
                  <input {...field('releasedBy')} placeholder="Releaser name" />
                </div>
                <div className="vl-field">
                  <label>Date Released</label>
                  <input type="date" {...field('dateReleased')} />
                </div>
                <div className="vl-field">
                  <label>Tested By</label>
                  <input {...field('testedBy')} placeholder="Tester name" />
                </div>
                <div className="vl-field">
                  <label>Date Tested</label>
                  <input type="date" {...field('dateTested')} />
                </div>
                <div className="vl-field full">
                  <label>Description<span className="req">*</span></label>
                  <textarea rows={3} {...field('description')} placeholder="Release notes / description" />
                  {errors.description && <span className="vl-field-err">{errors.description}</span>}
                </div>
              </div>

              {/* Posts sub-grid (visible only in edit mode) */}
              {editId && (
                <>
                  {/* Attachment row */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: '#555', whiteSpace: 'nowrap' }}>Attachment</label>
                    <input
                      type="text"
                      readOnly
                      value={attachFileName || 'No file selected'}
                      style={{
                        flex: 1, padding: '5px 9px', fontSize: 12.5, border: '1px solid #ccc',
                        borderRadius: 4, background: '#f8f8f8', color: attachFileName ? '#1a1a1a' : '#aaa',
                      }}
                    />
                    <button
                      className="ca-btn"
                      disabled={pdfParsing}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      {pdfParsing
                        ? <><i className="ti ti-loader-2" style={{ animation: 'spin 1s linear infinite' }} /> Parsing...</>
                        : <><span className="tb-i tb-i-blue">📎</span> Browse</>
                      }
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf"
                      style={{ display: 'none' }}
                      onChange={e => {
                        const file = e.target.files?.[0];
                        if (file) handlePdfFile(file);
                        e.target.value = '';
                      }}
                    />
                  </div>

                  <div className="vl-posts-hdr">
                    <span className="vl-posts-title">Posts</span>
                  </div>
                  <div className="vl-posts-table-wrap">
                    <table className="vl-posts-grid">
                      <thead>
                        <tr>
                          <th>Task Ticket ID</th>
                          <th>Category</th>
                          <th>Comment</th>
                          <th>Author</th>
                          <th style={{ width: 40 }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {(!detail?.posts || detail.posts.length === 0) ? (
                          <tr><td colSpan={5} className="vl-posts-empty">No posts yet.</td></tr>
                        ) : detail.posts.map(p => (
                          <tr key={p.oid}>
                            <td>{p.ticketId}</td>
                            <td>{p.categoryLabel ?? p.category}</td>
                            <td>{p.comment}</td>
                            <td>{p.author}</td>
                            <td className="act">
                              <button
                                className="vl-del-post"
                                title="Remove post"
                                onClick={() => handleDeletePost(p.oid)}
                              >
                                <i className="ti ti-trash" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>

            <div className="vl-dialog-footer">
              <button className="ca-btn" onClick={closeDialog}>
                <span className="tb-i tb-i-grey">↩︎</span> Cancel
              </button>
              <button className="ca-btn ca-btn-solid" onClick={submit} disabled={createMut.isPending || updateMut.isPending}>
                <span className="tb-i">💾</span> {
                  createMut.isPending || updateMut.isPending
                    ? (editId ? 'Updating…' : 'Saving…')
                    : (editId ? 'Update' : 'Save')
                }
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Add-post dialog ── */}
      {postDlgOpen && (
        <div className="vl-overlay" style={{ zIndex: 1300 }} onMouseDown={() => setPostDlgOpen(false)}>
          <div className="vl-dialog" style={{ maxWidth: 480 }} onMouseDown={e => e.stopPropagation()}>
            <div className="vl-dialog-hdr">
              <i className="ti ti-message-plus" />
              <span>Add Post</span>
              <i className="ti ti-x vl-dialog-x" onClick={() => setPostDlgOpen(false)} />
            </div>
            <div className="vl-dialog-body">
              <div className="vl-form-grid" style={{ gridTemplateColumns: '1fr' }}>
                <div className="vl-field">
                  <label>Task Ticket ID</label>
                  <input
                    value={postForm.ticketId}
                    onChange={e => setPostForm(f => ({ ...f, ticketId: e.target.value }))}
                    placeholder="Ticket number"
                  />
                </div>
                <div className="vl-field">
                  <label>Category</label>
                  <select
                    value={postForm.category}
                    onChange={e => setPostForm(f => ({ ...f, category: Number(e.target.value) }))}
                  >
                    {CATEGORY_OPTIONS.map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
                <div className="vl-field">
                  <label>Subject</label>
                  <input
                    value={postForm.subject}
                    onChange={e => setPostForm(f => ({ ...f, subject: e.target.value }))}
                    placeholder="Post subject"
                  />
                </div>
                <div className="vl-field">
                  <label>Comment</label>
                  <textarea
                    rows={4}
                    value={postForm.comment}
                    onChange={e => setPostForm(f => ({ ...f, comment: e.target.value }))}
                    placeholder="Post comment / details"
                  />
                </div>
              </div>
            </div>
            <div className="vl-dialog-footer">
              <button className="ca-btn" onClick={() => setPostDlgOpen(false)}>
                <span className="tb-i tb-i-grey">↩︎</span> Cancel
              </button>
              <button className="ca-btn ca-btn-solid" onClick={handleAddPost} disabled={addPostMut.isPending}>
                <span className="tb-i tb-i-green">＋</span> {addPostMut.isPending ? 'Adding…' : 'Add Post'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
